import { useEffect, useState } from 'react';
import type { Season } from '../../../../types';
import { updateSeason } from '../../../../store';
import { showToast } from '../../../../components/base/Toast';
import { formatDate } from '../../../../utils/format';
import { listCrops, listCultivationProcessesByCrop, applyCultivationProcess } from '../../../../api/crop';
import { listScheduleEventsBySeason } from '../../../../api/schedule';
import type { BackendCrop, BackendCultivationProcess } from '../../../../api/crop';
import type { BackendScheduleEvent } from '../../../../api/schedule';

interface AssignCultivationProcessModalProps {
  season: Season;
  htxId: string;
  onClose: () => void;
}

const SCHEDULE_STATUS_LABEL: Record<BackendScheduleEvent['status'], string> = {
  PLANNED: 'Kế hoạch',
  DONE: 'Đã thực hiện',
  SKIPPED: 'Bỏ qua',
  OVERDUE: 'Quá hạn',
};

/** HTX assigns a Crop + a CultivationProcess (built/managed by Kỹ sư/Admin) to
 * a farmer's season — reuses 100% of the existing apply-cultivation-process
 * plumbing, just adds the missing UI for it. */
export function AssignCultivationProcessModal({ season, htxId, onClose }: AssignCultivationProcessModalProps) {
  const [cropId, setCropId] = useState(season.cropId || '');
  const [crops, setCrops] = useState<BackendCrop[]>([]);
  const [processes, setProcesses] = useState<BackendCultivationProcess[]>([]);
  const [processId, setProcessId] = useState('');
  const [events, setEvents] = useState<BackendScheduleEvent[]>([]);
  const [savingCrop, setSavingCrop] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    listCrops(htxId).then(setCrops).catch(() => showToast('Không tải được danh mục cây trồng', 'error'));
  }, [htxId]);

  useEffect(() => {
    if (!cropId) {
      setProcesses([]);
      return;
    }
    listCultivationProcessesByCrop(cropId, false)
      .then((list) => setProcesses(list.filter((p) => p.status === 'ACTIVE')))
      .catch(() => showToast('Không tải được quy trình canh tác', 'error'));
  }, [cropId]);

  useEffect(() => {
    setLoadingEvents(true);
    listScheduleEventsBySeason(season.id)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [season.id]);

  async function saveCrop() {
    if (!cropId) return;
    setSavingCrop(true);
    try {
      await updateSeason(season.id, { cropId }, season.farmerId, htxId);
      showToast('Đã gán cây trồng cho mùa vụ', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lưu cây trồng thất bại', 'error');
    } finally {
      setSavingCrop(false);
    }
  }

  async function apply() {
    if (!processId) return;
    setApplying(true);
    try {
      const created = await applyCultivationProcess(season.id, processId);
      showToast(`Đã áp dụng quy trình — tạo ${created.length} bước trong lịch canh tác`, 'success');
      const refreshed = await listScheduleEventsBySeason(season.id);
      setEvents(refreshed);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Áp dụng quy trình thất bại', 'error');
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">Gán quy trình canh tác</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-slate-400">Mùa vụ: {season.name} ({season.maMuaVu})</p>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cây trồng (danh mục do Kỹ sư/Admin quản lý)</label>
            <div className="flex gap-2">
              <select value={cropId} onChange={(e) => setCropId(e.target.value)} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                <option value="">— Chọn cây trồng —</option>
                {crops.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={saveCrop}
                disabled={!cropId || cropId === season.cropId || savingCrop}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {savingCrop ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>

          {cropId && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quy trình canh tác</label>
              <div className="flex gap-2">
                <select value={processId} onChange={(e) => setProcessId(e.target.value)} className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                  <option value="">— Chọn quy trình —</option>
                  {processes.map((p) => (
                    <option key={p._id} value={p._id}>{p.name} (v{p.version})</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={apply}
                  disabled={!processId || applying}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                  {applying ? 'Đang áp dụng...' : 'Áp dụng'}
                </button>
              </div>
              {processes.length === 0 && (
                <p className="mt-1 text-xs text-slate-400">Chưa có quy trình canh tác nào (ACTIVE) cho cây trồng này.</p>
              )}
            </div>
          )}

          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-700 mb-2">Lịch canh tác hiện tại</p>
            {loadingEvents ? (
              <p className="text-xs text-slate-400">Đang tải...</p>
            ) : events.length === 0 ? (
              <p className="text-xs text-slate-400">Chưa áp dụng quy trình canh tác cho mùa vụ này.</p>
            ) : (
              <ul className="space-y-1.5">
                {events.map((ev) => (
                  <li key={ev._id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                    <span className="min-w-0 truncate text-slate-600">{ev.note ?? ev.eventType} · {formatDate(ev.plannedDate)}</span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        ev.status === 'DONE'
                          ? 'bg-emerald-100 text-emerald-700'
                          : ev.status === 'OVERDUE'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {SCHEDULE_STATUS_LABEL[ev.status]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3">
          <button type="button" onClick={onClose} className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}
